/**
 * debugRecorder.ts -- Recording system for debug sessions.
 *
 * Captures structured log entries from debugLogger during a recording window,
 * associates user-supplied problem descriptions, and exports in multiple
 * formats with per-recording category filtering.
 *
 * React integration: subscribe/getSnapshot for useSyncExternalStore.
 */

import {
  type DebugLogEntry,
  type LogCategory,
  getSnapshot as loggerGetSnapshot,
  subscribe as loggerSubscribe,
} from "./debugLogger.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ExportFormat = "json" | "jsonl" | "text";

const ALL_CATEGORIES: LogCategory[] = ["js", "gpu", "wasm"];

export interface DebugRecording {
  id: string;
  startTs: number;
  endTs: number;
  description: string;
  entries: DebugLogEntry[];
  /** Per-recording category filter for export. All enabled by default. */
  enabledCategories: Set<LogCategory>;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let recordings: DebugRecording[] = [];
let activeRecordingId: string | null = null;
let unsubLogger: (() => void) | null = null;
/** Logger snapshot length at last check -- used to detect new entries. */
let lastKnownLength = 0;

// ---------------------------------------------------------------------------
// Persistence (localStorage crash recovery)
//
// Recordings are flushed to localStorage every 2 s while dirty (catches hard
// crashes where beforeunload never fires) and synchronously on beforeunload.
// On reload, any recording with endTs === 0 was active at crash time and is
// marked `recovered` so the UI can surface it.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "poseref-debug-recordings";

interface StoredRecording {
  id: string;
  startTs: number;
  endTs: number;
  description: string;
  entries: DebugLogEntry[];
  enabledCategories: LogCategory[];
}

function toStored(rec: DebugRecording): StoredRecording {
  return {
    id: rec.id,
    startTs: rec.startTs,
    endTs: rec.endTs,
    description: rec.description,
    entries: rec.entries,
    enabledCategories: [...rec.enabledCategories] as LogCategory[],
  };
}

function fromStored(s: StoredRecording): DebugRecording {
  return {
    id: s.id,
    startTs: s.startTs,
    endTs: s.endTs,
    description: s.description,
    entries: Array.isArray(s.entries) ? s.entries : [],
    enabledCategories: new Set(
      Array.isArray(s.enabledCategories) ? s.enabledCategories : ALL_CATEGORIES,
    ),
  };
}

let dirty = false;

function flushToStorage(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recordings.map(toStored)));
    dirty = false;
  } catch {
    // Quota exceeded — try persisting only the active recording to preserve crash data
    if (activeRecordingId) {
      const active = recordings.find((r) => r.id === activeRecordingId);
      if (active) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify([toStored(active)]));
          dirty = false;
        } catch {
          /* quota truly exhausted */
        }
      }
    }
  }
}

function loadFromStorage(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return;
    recordings = (parsed as StoredRecording[]).map((s) => fromStored(s));
    snapshot = [...recordings];
  } catch {
    /* malformed data — start fresh */
  }
}

loadFromStorage();
setInterval(() => {
  if (dirty) flushToStorage();
}, 2000);
window.addEventListener("beforeunload", flushToStorage);

// ---------------------------------------------------------------------------
// Subscriber management (useSyncExternalStore)
// ---------------------------------------------------------------------------

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const fn of listeners) fn();
}

export function subscribe(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

let snapshot: readonly DebugRecording[] = recordings;

export function getSnapshot(): readonly DebugRecording[] {
  return snapshot;
}

function updateSnapshot(): void {
  snapshot = [...recordings];
  dirty = true;
  notify();
}

// ---------------------------------------------------------------------------
// Recording API
// ---------------------------------------------------------------------------

export function isRecording(): boolean {
  return activeRecordingId != null;
}

export function getActiveRecordingId(): string | null {
  return activeRecordingId;
}

export function startRecording(): string {
  if (activeRecordingId != null) stopRecording();

  const id = crypto.randomUUID();
  const recording: DebugRecording = {
    id,
    startTs: Date.now(),
    endTs: 0,
    description: "",
    entries: [],
    enabledCategories: new Set(ALL_CATEGORIES),
  };

  recordings = [...recordings, recording];
  activeRecordingId = id;
  lastKnownLength = loggerGetSnapshot().length;

  // Subscribe to logger changes and capture new entries
  unsubLogger = loggerSubscribe(() => {
    const allEntries = loggerGetSnapshot();
    const rec = recordings.find((r) => r.id === activeRecordingId);
    if (!rec) return;

    // Append only entries added since last check
    if (allEntries.length > lastKnownLength) {
      const newEntries = allEntries.slice(lastKnownLength);
      for (const entry of newEntries) {
        rec.entries.push(entry);
      }
      lastKnownLength = allEntries.length;
      updateSnapshot();
    }
  });

  updateSnapshot();
  return id;
}

export function stopRecording(): void {
  if (activeRecordingId == null) return;
  const rec = recordings.find((r) => r.id === activeRecordingId);
  if (rec) rec.endTs = Date.now();

  if (unsubLogger) {
    unsubLogger();
    unsubLogger = null;
  }

  activeRecordingId = null;
  updateSnapshot();
  flushToStorage();
}

export function setDescription(id: string, desc: string): void {
  const rec = recordings.find((r) => r.id === id);
  if (!rec) return;
  rec.description = desc;
  updateSnapshot();
}

export function toggleCategory(id: string, cat: LogCategory): void {
  const rec = recordings.find((r) => r.id === id);
  if (!rec) return;
  if (rec.enabledCategories.has(cat)) {
    rec.enabledCategories.delete(cat);
  } else {
    rec.enabledCategories.add(cat);
  }
  updateSnapshot();
}

export function deleteRecording(id: string): void {
  if (activeRecordingId === id) stopRecording();
  recordings = recordings.filter((r) => r.id !== id);
  updateSnapshot();
  flushToStorage();
}

export function clearRecordings(): void {
  if (activeRecordingId != null) stopRecording();
  recordings = [];
  updateSnapshot();
  flushToStorage();
}

// ---------------------------------------------------------------------------
// Size computation
// ---------------------------------------------------------------------------

function filteredEntries(rec: DebugRecording): DebugLogEntry[] {
  if (rec.enabledCategories.size === ALL_CATEGORIES.length) return rec.entries;
  return rec.entries.filter((e) => rec.enabledCategories.has(e.cat));
}

/** Full (unfiltered) byte size estimate. */
export function getRecordingSize(id: string): number {
  const rec = recordings.find((r) => r.id === id);
  if (!rec) return 0;
  return estimateSize(rec.entries);
}

/** Filtered byte size estimate (respects enabledCategories). */
export function getFilteredSize(id: string): number {
  const rec = recordings.find((r) => r.id === id);
  if (!rec) return 0;
  return estimateSize(filteredEntries(rec));
}

function estimateSize(entries: DebugLogEntry[]): number {
  // Fast estimate: ~100 bytes base per entry + data payload
  let size = 0;
  for (const e of entries) {
    size += 80 + e.tag.length + e.msg.length;
    if (e.data) {
      for (const v of Object.values(e.data)) {
        if (typeof v === "string") size += v.length;
        else if (typeof v === "number") size += 8;
        else size += 20;
      }
    }
  }
  return size;
}

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function formatTime(ts: number): string {
  const d = new Date(ts);
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return `${h}:${m}:${s}.${ms}`;
}

function formatEntryText(e: DebugLogEntry): string {
  let line = `[${formatTime(e.ts)}] [${e.cat.toUpperCase()}] [${e.sev.toUpperCase()}] ${e.tag}: ${e.msg}`;
  if (e.count > 1) line += ` (x${e.count})`;
  if (e.data) {
    const compact = JSON.stringify(e.data);
    if (compact.length <= 200) {
      line += ` ${compact}`;
    } else {
      line += ` ${compact.slice(0, 197)}...`;
    }
  }
  return line;
}

function serializeEntry(e: DebugLogEntry): Record<string, unknown> {
  return {
    id: e.id,
    ts: e.ts,
    cat: e.cat,
    sev: e.sev,
    tag: e.tag,
    msg: e.msg,
    ...(e.data ? { data: e.data } : {}),
    ...(e.count > 1 ? { count: e.count } : {}),
  };
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

function exportRecordingEntries(
  rec: DebugRecording,
  format: ExportFormat,
): string {
  const entries = filteredEntries(rec);
  const cats = [...rec.enabledCategories].join(",");

  switch (format) {
    case "json":
      return JSON.stringify(
        {
          description: rec.description || "(no description)",
          startTs: rec.startTs,
          endTs: rec.endTs,
          durationMs: rec.endTs ? rec.endTs - rec.startTs : null,
          categories: cats,
          entryCount: entries.length,
          entries: entries.map(serializeEntry),
        },
        null,
        2,
      );

    case "jsonl": {
      const header = JSON.stringify({
        _type: "recording_header",
        description: rec.description || "(no description)",
        startTs: rec.startTs,
        endTs: rec.endTs,
        categories: cats,
        entryCount: entries.length,
      });
      const lines = [header];
      for (const e of entries) {
        lines.push(JSON.stringify(serializeEntry(e)));
      }
      return lines.join("\n") + "\n";
    }

    case "text": {
      const lines: string[] = [];
      lines.push(`=== RECORDING: ${rec.description || "(no description)"} ===`);
      lines.push(
        `Started: ${formatTime(rec.startTs)}  Ended: ${rec.endTs ? formatTime(rec.endTs) : "in progress"}`,
      );
      lines.push(`Categories: ${cats}  Entries: ${entries.length}`);
      lines.push("");
      for (const e of entries) {
        lines.push(formatEntryText(e));
      }
      return lines.join("\n") + "\n";
    }
  }
}

export function exportRecording(id: string, format: ExportFormat): string {
  const rec = recordings.find((r) => r.id === id);
  if (!rec) return "";
  return exportRecordingEntries(rec, format);
}

export function exportAll(format: ExportFormat): string {
  if (recordings.length === 0) return "";

  switch (format) {
    case "json":
      return JSON.stringify(
        {
          exportTs: Date.now(),
          recordingCount: recordings.length,
          recordings: recordings.map((rec) => {
            const entries = filteredEntries(rec);
            const cats = [...rec.enabledCategories].join(",");
            return {
              description: rec.description || "(no description)",
              startTs: rec.startTs,
              endTs: rec.endTs,
              durationMs: rec.endTs ? rec.endTs - rec.startTs : null,
              categories: cats,
              entryCount: entries.length,
              entries: entries.map(serializeEntry),
            };
          }),
        },
        null,
        2,
      );

    case "jsonl": {
      const parts: string[] = [];
      for (const rec of recordings) {
        parts.push(exportRecordingEntries(rec, "jsonl"));
        parts.push("");
      }
      return parts.join("\n");
    }

    case "text": {
      const parts: string[] = [];
      for (const rec of recordings) {
        parts.push(exportRecordingEntries(rec, "text"));
        parts.push("");
      }
      return parts.join("\n");
    }
  }
}

// ---------------------------------------------------------------------------
// File download helper
// ---------------------------------------------------------------------------

export function downloadExport(
  content: string,
  filename: string,
  format: ExportFormat,
): void {
  const mimeTypes: Record<ExportFormat, string> = {
    json: "application/json",
    jsonl: "application/x-ndjson",
    text: "text/plain",
  };
  const blob = new Blob([content], { type: mimeTypes[format] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
